import { CheckPicker, Divider, Loader, SelectPicker, Toggle } from "rsuite";
import ActualVsCommited from "./details/actual-vs-commited";
import SixMonthTrend from "./details/six-month-trend";
import { useEffect, useState } from "react";
import ActualVsCommitedTab from "./actual-vs-commited";
import {
  useComplianceFilters,
  useVolumeComplianceData,
  useVolumeComplianceDistrictsData,
} from "../../../react-query/hooks/HCompliance";
import useResponsiveWidth from "../../../react-query/hooks/useResponsiveWidth";
import { defaultVendor } from "../../../utils/constants";
import { appInsights } from "../../../router/router";
import AllocationFilters from "./allocation-compliance/allocationFilters";
import AllocationCompliance from "./allocation-compliance";
import { sortCommodityFirst, getDefaultCommodity, COPPER_WIRE } from "../../../utils/helpers/commodityOrder";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../services/redux/hooks/hooks";
import { updateAllocationComplianceDetails } from "../../../services/redux/reducer/allocationCompliance.reducer";
import { isParameter } from "typescript";
import { useLocation } from "react-router-dom";
import { trackTabClick } from "../../../utils/helpers/trackingEvent";

const VolumeCompliance = () => {
  const AllocationComplianceData = useAppSelector(
    (state) => state.allocationCompliance
  );
  const dispatch = useAppDispatch();
  const width = useResponsiveWidth();
  const location = useLocation();
  const hiddenParam = location.state?.hiddenParam;
  const [navItem, setNavItem] = useState<string>(
    hiddenParam === undefined || hiddenParam === ""
      ? "actualCommitedVol"
      : hiddenParam
  );
  // Initialize with the app-wide default commodity so the first filters API
  // call already requests the correct commodity, avoiding a throwaway
  // `commodity=""` request that is immediately refetched with the default.
  const [commodity, setCommodity] = useState<string>(COPPER_WIRE);
  const [vendor, setVendor] = useState<string>("");
  const [districts, setDistricts] = useState<any>([]);
  const [month, setMonth] = useState<any>([]);
  const [lastRefreshDate, setLastRefreshData] = useState(null);
  const [filters, setFilters] = useState<{
    commodity: string;
    district?: any;
    vendor: any;
    month: any;
  }>({
    commodity: "",
    district: [],
    vendor: [],
    month: [],
  });
  const [isDownload, setIsDownload] = useState<boolean>(false);
  const { complianceFilters, filtersLoaded } = useComplianceFilters(
    localStorage.getItem("userEmail"),
    "volume",
    commodity
  );

  // Latest available month from the filters API. Used as a render-time fallback
  // so the month is ALWAYS selected in the UI and ALWAYS sent to the APIs,
  // regardless of the timing of the default-setting effects below (the filters
  // for the default commodity can arrive on a later fetch).
  const defaultMonth = complianceFilters?.dates?.[0]?.["year_date"];
  const selectedMonth = (Array.isArray(month) ? month[0] : month) || defaultMonth;
  const effectiveMonth = selectedMonth ? [selectedMonth] : [];

  const { volumeComplianceData, isLoading } = useVolumeComplianceData(
    navItem,
    filters?.commodity,
    filters?.district && filters.district.length !== 0
      ? filters.district
      : complianceFilters && complianceFilters.districts
        ? complianceFilters.districts.map((ele: any) => ele.district)
        : [],
    effectiveMonth
  );
  const { volumeDataForDistricts, isLoadingVolume } =
    useVolumeComplianceDistrictsData(
      navItem,
      filters?.commodity,
      vendor,
      filters?.district,
      effectiveMonth
    );
  useEffect(() => {
    if (
      !filtersLoaded &&
      Object?.keys(complianceFilters)?.length > 0 &&
      filters.commodity === ""
    ) {
      setCommodity(getDefaultCommodity(complianceFilters?.commodity) ?? "");
      setMonth(complianceFilters?.dates?.[0]?.["year_date"]);
      setFilters({
        commodity: getDefaultCommodity(complianceFilters?.commodity) ?? "",
        vendor: complianceFilters?.vendor?.map((item: any) => item?.vendor),
        month: [complianceFilters?.dates?.[0]?.["year_date"]],
      });
    }
  }, [filtersLoaded]);

  // Fool-proof default month: set the latest month as soon as dates are
  // available and the user hasn't selected a month yet. This handles the case
  // where dates arrive on a later fetch (e.g. after the default commodity is
  // applied) and the commodity-based guard above no longer runs.
  useEffect(() => {
    const defaultMonth = complianceFilters?.dates?.[0]?.["year_date"];
    const monthNotSet =
      month === undefined ||
      month === null ||
      (Array.isArray(month) && month.length === 0);
    if (defaultMonth && monthNotSet) {
      setMonth(defaultMonth);
      setFilters((prev: any) => ({
        ...prev,
        month: [defaultMonth],
      }));
    }
  }, [complianceFilters?.dates?.[0]?.["year_date"]]);

  useEffect(() => {
    if (navItem === "details") {
      if (districts?.length === 1 && Array.isArray(districts)) {
        setDistricts(districts[0]);
        setVendor(complianceFilters?.vendor[0]?.vendor);
      } else {
        if (districts?.length > 1 && Array.isArray(districts)) setDistricts([]);
        setVendor(complianceFilters?.vendor[0]?.vendor);
      }
    } else {
      if (
        filters?.district?.length === 0 &&
        filters?.commodity === "" &&
        complianceFilters?.commodity
      ) {
        setCommodity(getDefaultCommodity(complianceFilters?.commodity) ?? "");
        setDistricts([]);
        setFilters({
          commodity: getDefaultCommodity(complianceFilters?.commodity) ?? "",
          vendor: complianceFilters?.vendor?.map((item: any) => item?.vendor),
          month: [complianceFilters?.dates?.[0]?.["year_date"]],
          district: [],
        });
      } else if (filters?.district?.length > 0 && districts?.length === 0) {
        setFilters({
          ...filters,
          district: [],
        });
        setDistricts([]);
      } else if (
        filters?.district?.length === 0 &&
        filters?.commodity !== "" &&
        filters?.month?.length !== 0
      ) {
        setFilters({
          ...filters,
          district: [],
        });
        setDistricts([]);
      }
    }
  }, [navItem]);

  useEffect(() => {
    if (complianceFilters?.refreshed_date) {
      setLastRefreshData(complianceFilters?.refreshed_date);
    }
  }, [complianceFilters?.refreshed_date]);

  let latestMonth =
    complianceFilters?.dates && complianceFilters?.dates[0]?.["year_date"];

  const appInsightsFunction = (module: string, value: string) => {
    appInsights.trackEvent({
      name: `Compliance Monitoring: Volume Compliance Detailed ${module}`,
      properties: {
        description: `${localStorage.getItem(
          "userEmail"
        )} has downloaded "${value}" File`,
      },
    });
  };

  const handleTabonClick = (tabName: string, tabLabel: string) => {
    setNavItem(tabName);
    trackTabClick(tabLabel, "Volume Compliance");
  };

  return (
    <div className="volume-compliance-container flex flex-col justify-stretch items-stretch h-full quote-assessment">
      {(isLoading ||
        isLoadingVolume ||
        filtersLoaded ||
        (navItem === "allocationCompliance" &&
          (AllocationComplianceData.filtersLoaded ||
            AllocationComplianceData.dataLoaded))) && (
          <Loader
            backdrop
            content="Loading..."
            size="md"
            vertical
            className="text-black font-bold"
            style={{ opacity: 0.8, zIndex: 9999 }}
          />
        )}
      <div className="grow shrink-0 basis-[20%] h-[23vh] ps-[3%] pr-[5%] pt-[1.5%] flex flex-col flex-nowrap justify-start items-start gap-0 shadow-normal relative">
        <div className="flex justify-between items-center w-full">
          <div className="flex flex-col w-full">
            <div className="font-normal text-[2vw]">Volume Metrics</div>
            <div className="flex items-center">
              <span className="font-bold text-fs16 text-[#858585]">
                Purchase data last refreshed:{" "}
              </span>
              <span className="font-bold underline text-fs16 text-deloitteGreen pl-[0.25vh]">
                {lastRefreshDate}
              </span>
            </div>
          </div>
          {navItem === "allocationCompliance" ? (
            <AllocationFilters />
          ) : (
            <div className="flex justify-start items-center gap-8">
              <div className="flex flex-col justify-start items-start max-w-[max(180px,10vw)]">
                <label className="font-bold text-[0.8vw]">Commodity</label>

                <SelectPicker
                  menuAutoWidth
                  className="dropdown-responsive-width"
                  labelKey="commodity"
                  valueKey="commodity"
                  value={commodity}
                  placeholder="Select commodity"
                  cleanable={false}
                  style={{ width: width }}
                  disabledItemValues={[]}
                  onChange={(value: string | null) => {
                    if (value) {
                      setCommodity(value);
                      setFilters({
                        ...filters,
                        commodity: value,
                        vendor: defaultVendor[value]
                          ? defaultVendor[value]
                          : "",
                      });
                      setVendor(
                        defaultVendor[value] ? defaultVendor[value] : ""
                      );
                    }
                  }}
                  data={sortCommodityFirst(complianceFilters?.commodity)}
                ></SelectPicker>
              </div>

              <div className="flex flex-col justify-start items-start max-w-[max(180px,10vw)]">
                <label className="font-bold text-[0.8vw]">District</label>

                {navItem === "details" ? (
                  <SelectPicker
                    className="dropdown-responsive-width"
                    menuAutoWidth
                    placeholder="Select District"
                    labelKey="district"
                    style={{ width: width }}
                    cleanable={false}
                    value={districts}
                    onChange={(value: string | null) => {
                      if (value) {
                        setFilters({
                          commodity,
                          district: value,
                          month,
                          vendor: vendor,
                        });
                        setDistricts(value);
                      }
                    }}
                    menuStyle={{ width: "120px" }}
                    menuMaxHeight={200}
                    valueKey="district"
                    data={complianceFilters?.districts ?? []}
                  />
                ) : (
                  <CheckPicker
                    menuAutoWidth
                    placeholder="All"
                    className="dropdown-responsive-width"
                    labelKey="district"
                    cleanable={true}
                    style={{ width: width }}
                    onClean={() => {
                      setFilters({
                        ...filters,
                        district: [],
                      });
                    }}
                    value={Array.isArray(districts) ? districts : [districts]}
                    valueKey="district"
                    onClose={() => {
                      setFilters({
                        commodity,
                        district: districts,
                        month,
                        vendor: vendor,
                      });
                    }}
                    menuStyle={{ width: "120px" }}
                    menuMaxHeight={200}
                    onChange={(value: string[]) => {
                      setDistricts(value.length > 0 ? value : []);
                    }}
                    data={complianceFilters?.districts ?? []}
                  />
                )}
              </div>

              <div className="flex flex-col justify-start items-start max-w-[max(180px,10vw)]">
                <label className="font-bold text-[0.8vw]">Month</label>
                {/* {navItem != "details" ? (
                  <CheckPicker
                    countable={false}
                    menuAutoWidth
                    className="dropdown-responsive-width"
                    placeholder="All"
                    style={{ width: width }}
                    cleanable={true}
                    value={Array.isArray(month) ? month : [month]}
                    labelKey="year_date"
                    valueKey="year_date"
                    onClean={() => {
                      setFilters({
                        ...filters,
                        month: null,
                      });
                    }}
                    onClose={() => {
                      setFilters({
                        commodity,
                        district: districts,
                        month,
                        vendor: [...filters?.vendor],
                      });
                    }}
                    onChange={(value: string[]) => {
                      let result = value?.filter((item) => item != null);
                      setMonth(result?.length > 0 ? result : null);
                    }}
                    menuMaxHeight={150}
                    data={complianceFilters?.dates ?? []}
                  />
                ) : ( */}
                <SelectPicker
                  menuAutoWidth
                  placeholder="Select Month"
                  labelKey="year_date"
                  className="dropdown-responsive-width"
                  cleanable={false}
                  style={{ width: width }}
                  value={selectedMonth}
                  onChange={(value: string | null) => {
                    if (value) {
                      setFilters({
                        commodity,
                        district: districts,
                        month: value,
                        vendor: vendor,
                      });
                      setMonth(value);
                    }
                  }}
                  menuMaxHeight={150}
                  valueKey="year_date"
                  data={complianceFilters?.dates ?? []}
                />
                {/* )} */}
              </div>
            </div>
          )}
        </div>
        {!isLoading && !isLoadingVolume && (
          <div className="flex justify-between items-end">
            <div className="absolute bottom-0 flex justify-start items-center gap-6 text-black font-normal text-[1.8vh]">
              <span
                className={
                  (navItem === "actualCommitedVol"
                    ? "font-bold border-b-2 border-black"
                    : "") + " cursor-pointer pb-[1vh]"
                }
                onClick={() =>
                  handleTabonClick(
                    "actualCommitedVol",
                    "Committed vs Actual Volume"
                  )
                }
              >
                Committed vs Actual Volume
              </span>
              <span
                className={
                  (navItem === "details"
                    ? "font-bold border-b-2 border-black"
                    : "") + " cursor-pointer pb-[1vh]"
                }
                onClick={() => handleTabonClick("details", "Details")}
              >
                Details
              </span>
              <span
                className={
                  (navItem === "allocationCompliance"
                    ? "font-bold border-b-2 border-black"
                    : "") + " cursor-pointer pb-[1vh]"
                }
                onClick={() =>
                  handleTabonClick(
                    "allocationCompliance",
                    "Market Share Compliance"
                  )
                }
              >
                Market Share
              </span>
            </div>
            {navItem === "allocationCompliance" && (
              <div className={`${"absolute bottom-2 right-[5vw] flex gap-10"}`}>
                <div
                  onClick={() =>
                    dispatch(
                      updateAllocationComplianceDetails({
                        ...AllocationComplianceData,
                        isDownload: AllocationComplianceData.isData,
                      })
                    )
                  }
                  className={`text-white bold text-[0.8vw] bg-[#86BC25] p-[0.4vw] rounded-md flex items-center ${!AllocationComplianceData.isData
                      ? "cursor-not-allowed opacity-50"
                      : "cursor-pointer"
                    }`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="1vw"
                    height="1vw"
                    viewBox="0 0 12 16"
                    fill="none"
                  >
                    <path
                      d="M14.6668 5.33464V9.20797C14.2602 8.97464 13.8135 8.81464 13.3335 8.72797V5.33464H2.66683V12.0013H8.72683C8.6935 12.2213 8.66683 12.4413 8.66683 12.668C8.66683 12.8946 8.6935 13.1146 8.72683 13.3346H2.66683C1.9335 13.3346 1.3335 12.7413 1.3335 12.0013V4.0013C1.3335 3.2613 1.92683 2.66797 2.66683 2.66797H6.66683L8.00016 4.0013H13.3335C14.0668 4.0013 14.6668 4.59464 14.6668 5.33464ZM13.3335 10.668H12.0002V13.3346H10.6668L12.6668 15.3346L14.6668 13.3346H13.3335V10.668Z"
                      fill="white"
                    />
                  </svg>
                  <span className="ml-[3px]">Download</span>
                </div>
                <div className="text-fs12 mt-1">
                  <span className={`text-[0.7vw] font-bold`}>All Supplier</span>
                  <Toggle
                    checked={AllocationComplianceData.isPartnerView}
                    onChange={(value) => {
                      dispatch(
                        updateAllocationComplianceDetails({
                          ...AllocationComplianceData,
                          filter: {
                            commodity: "Steel Conduit",
                            district: value
                              ? AllocationComplianceData.filter.district
                                .length === 1
                                ? AllocationComplianceData.filter.district
                                : ["Atlanta"]
                              : AllocationComplianceData.filter.district,
                            baseline: AllocationComplianceData.filter.baseline,
                            trendView:
                              AllocationComplianceData.filter.trendView,
                            // baseSku: AllocationComplianceData.filter.baseSku,
                            baseSku: "",
                          },
                          isPartnerView: value,
                        })
                      );
                    }}
                    size={"md"}
                    className="px-2"
                    color="green"
                  />
                  <span
                    className={`text-[0.7vw] font-bold`}
                  // ${AllocationComplianceData.isPartnerView && "text-[green]"}
                  >
                    Partner Supplier
                  </span>
                </div>
              </div>
            )}
            {navItem === "details" &&
              (Array.isArray(districts)
                ? districts.length > 0
                : districts !== null) && (
                <div
                  className={`${"absolute bottom-2 right-[5vw] flex gap-10"}`}
                >
                  <div
                    onClick={() => setIsDownload(true)}
                    className={`text-white bold text-[0.8vw] bg-[#86BC25] p-[0.4vw] rounded-md flex items-center ${"cursor-pointer"}`}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="1vw"
                      height="1vw"
                      viewBox="0 0 12 16"
                      fill="none"
                    >
                      <path
                        d="M14.6668 5.33464V9.20797C14.2602 8.97464 13.8135 8.81464 13.3335 8.72797V5.33464H2.66683V12.0013H8.72683C8.6935 12.2213 8.66683 12.4413 8.66683 12.668C8.66683 12.8946 8.6935 13.1146 8.72683 13.3346H2.66683C1.9335 13.3346 1.3335 12.7413 1.3335 12.0013V4.0013C1.3335 3.2613 1.92683 2.66797 2.66683 2.66797H6.66683L8.00016 4.0013H13.3335C14.0668 4.0013 14.6668 4.59464 14.6668 5.33464ZM13.3335 10.668H12.0002V13.3346H10.6668L12.6668 15.3346L14.6668 13.3346H13.3335V10.668Z"
                        fill="white"
                      />
                    </svg>
                    <span className="ml-[3px]">Download</span>
                  </div>
                </div>
              )}
          </div>
        )}
      </div>
      {navItem === "details" && (
        <div className="bg-[#F1F1F1] grow shrink p-[2%] flex flex-col flex-nowrap justify-start items-start gap-6 h-[77vh] w-full overflow-hidden">
          <div className="flex  h-[95%] w-full gap-4">
            {Array.isArray(districts) ? (
              <div className="shadow-module-card  bg-white h-full w-full flex justify-center text-fs32 items-center">
                {`Please select a ${Array.isArray(districts) ? "single district" : ""
                  } ${Array.isArray(month) && Array.isArray(districts) ? "and " : ""
                  } ${Array.isArray(month) ? "single month" : ""} to view data`}
              </div>
            ) : (
              <>
                <div className="shadow-module-card border overflow-scroll border-[#bebebe] bg-white h-full w-[50%] rounded-md p-[40px] below-1200:p-[20px] flex flex-col justify-start items-start">
                  <ActualVsCommited
                    selectedDistrict={districts}
                    vendorData={volumeDataForDistricts?.districtData?.tableRes}
                    vendorGraphData={
                      volumeDataForDistricts?.districtData?.grapghres
                    }
                    commodity={commodity}
                    appInsightsFunc={appInsightsFunction}
                  />
                </div>
                <div className="shadow-module-card border overflow-scroll border-[#bebebe] bg-white h-full w-[50%] rounded-md p-[40px] below-1200:p-[20px] flex flex-col justify-between items-start">
                  <SixMonthTrend
                    vendor={vendor}
                    setVendor={setVendor}
                    commodity={commodity}
                    vendorDataTable={volumeDataForDistricts?.allVendorData}
                    totalVendors={complianceFilters?.vendor
                      ?.filter((ele: any) => ele?.vendor !== null)
                      .map((item: any) => item?.vendor)}
                    vendorData={volumeDataForDistricts?.vendorData?.graphResForChoosenVendor?.map(
                      (item: any) => {
                        return { ...item, month: item?.month?.slice(0, 3) };
                      }
                    )}
                    appInsightsFunc={appInsightsFunction}
                    isDownload={isDownload}
                    setIsDownload={setIsDownload}
                    district={
                      Array.isArray(districts) ? districts[0] : districts
                    }
                    month={month}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {navItem === "actualCommitedVol" &&
        !isLoading &&
        volumeComplianceData && (
          <ActualVsCommitedTab
            month={selectedMonth ? [selectedMonth] : null}
            latestMonth={latestMonth}
            data={volumeComplianceData}
            commodity={commodity}
            district={districts}
          />
        )}
      {navItem === "allocationCompliance" && <AllocationCompliance />}
    </div>
  );
};

export default VolumeCompliance;
